import React from 'react';
import {
  ownerAPI,
  propertyAPI,
  contractAPI,
  propertyUnitAPI,
  propertyOwnershipAPI,
  propertyAttachmentAPI,
  organizationDocumentAPI,
  staffAPI,
  ownerAccountAPI,
} from '../../services/api';
import { getFileUrl } from '../../utils/fileUrl';

/**
 * Node model for the tree browser.
 *
 * Every node is:
 *   { id, label, sublabel, icon, meta, to?, loadChildren? }
 *
 * A node with `loadChildren` opens another column; a node without one is a
 * leaf. `to` adds a link to the module's own page.
 *
 * `t` is passed in so labels stay translated without the data layer importing
 * react-i18next.
 */

const PAGE = 50;

/** Strips the actions a user is not allowed to perform on `module`. */
const guardNode = (node, module, can) => ({
  ...node,
  create: can(module, 'Create') ? node.create : undefined,
  createIntake: can(module, 'Create') ? node.createIntake : undefined,
  createContract: can(module, 'Create') ? node.createContract : undefined,
  edit: can(module, 'Edit') ? node.edit : undefined,
  intake: can(module, 'View') ? node.intake : undefined,
  contractIntake: can(module, 'View') ? node.contractIntake : undefined,
  ledger: can('contracts', 'View') ? node.ledger : undefined,
  report: can('properties', 'View') ? node.report : undefined,
  remove: can(module, 'Delete') ? node.remove : undefined,
});


const dash = (value) => (value === null || value === undefined || value === '' ? null : value);

const propertyLabel = (p) => dash(p.nameAr) || dash(p.nameEng) || dash(p.code) || `#${p.id}`;

// ---- field schemas (shared by the create and edit dialogs) --------------

const f = (t, name, key, icon, type = 'text', required = false) => ({
  name,
  label: t(`tree.fields.${key}`),
  placeholder: t(`tree.fields.${key}`),
  icon,
  type,
  required,
});

const unitFields = (t) => [
  f(t, 'unitNumber', 'unitNumber', 'fa-hashtag', 'text', true),
  f(t, 'floorNumber', 'floorNumber', 'fa-stairs', 'number'),
  f(t, 'unitArea', 'unitArea', 'fa-ruler-combined', 'number'),
  f(t, 'numberOfRooms', 'numberOfRooms', 'fa-bed', 'number'),
  f(t, 'unitDirection', 'unitDirection', 'fa-compass'),
  f(t, 'unitFinishing', 'unitFinishing', 'fa-paint-roller'),
  f(t, 'countOfParking', 'countOfParking', 'fa-square-parking', 'number'),
  f(t, 'electricityMeter', 'electricityMeter', 'fa-bolt'),
  f(t, 'waterMeter', 'waterMeter', 'fa-droplet'),
  f(t, 'furnished', 'furnished', 'fa-couch', 'checkbox'),
];

const ownerFields = (t) => [
  f(t, 'ownershipNameAr', 'nameAr', 'fa-user', 'text', true),
  f(t, 'ownershipNameEng', 'nameEng', 'fa-user', 'text', true),
  f(t, 'identityNumber', 'identityNumber', 'fa-id-card'),
  f(t, 'mobileNumber', 'mobileNumber', 'fa-mobile-screen'),
  f(t, 'emailId', 'email', 'fa-envelope'),
  f(t, 'bankAccountNumber', 'bankAccount', 'fa-building-columns'),
  f(t, 'streetName', 'streetName', 'fa-road'),
  f(t, 'isAgent', 'isAgent', 'fa-user-shield', 'checkbox'),
];

// Numbers arrive from inputs as strings; send null rather than ''.
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const str = (v) => (v === '' || v === undefined ? null : v);

const unitPayload = (values, propertyId) => ({
  propertyId,
  unitNumber: str(values.unitNumber),
  floorNumber: num(values.floorNumber),
  unitArea: num(values.unitArea),
  numberOfRooms: num(values.numberOfRooms),
  unitDirection: str(values.unitDirection),
  unitFinishing: str(values.unitFinishing),
  countOfParking: num(values.countOfParking),
  electricityMeter: str(values.electricityMeter),
  waterMeter: str(values.waterMeter),
  furnished: Boolean(values.furnished),
});

const ownerPayload = (values, propertyId) => ({
  propertyId,
  ownershipNameAr: values.ownershipNameAr,
  ownershipNameEng: values.ownershipNameEng,
  identityNumber: str(values.identityNumber),
  mobileNumber: str(values.mobileNumber),
  emailId: str(values.emailId),
  bankAccountNumber: str(values.bankAccountNumber),
  streetName: str(values.streetName),
  isAgent: Boolean(values.isAgent),
});

// ---- attachments --------------------------------------------------------

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
const VIDEO_EXT = ['mp4', 'webm', 'ogg', 'mov'];

const extensionOf = (path) => (path || '').split('.').pop().toLowerCase();

const kindOf = (path) => {
  const ext = extensionOf(path);
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (VIDEO_EXT.includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  return 'file';
};

const KIND_ICON = {
  image: 'fa-file-image',
  video: 'fa-file-video',
  pdf: 'fa-file-pdf',
  file: 'fa-paperclip',
};

const attachmentFields = (t, requireFile) => [
  {
    name: 'name',
    label: t('tree.fields.attachmentName'),
    placeholder: t('tree.fields.attachmentName'),
    icon: 'fa-tag',
    type: 'text',
    required: true,
  },
  {
    name: 'file',
    label: requireFile ? t('tree.fields.file') : t('tree.fields.replaceFile'),
    type: 'file',
    accept: 'image/*,video/*,application/pdf',
    required: requireFile,
  },
];

const attachmentBody = (values, propertyId) => {
  const body = new FormData();
  if (propertyId !== undefined) body.append('propertyId', propertyId);
  body.append('name', values.name || '');
  if (values.file) body.append('file', values.file);
  return body;
};

/** Inline preview + open/download links, rendered inside the details dialog. */
const attachmentPreview = (attachment, t) => {
  const url = getFileUrl(attachment.path);
  const kind = kindOf(attachment.path);

  let media;
  if (kind === 'image') {
    media = <img src={url} alt={attachment.name} />;
  } else if (kind === 'video') {
    media = <video src={url} controls preload="metadata" />;
  } else if (kind === 'pdf') {
    media = (
      <span className="ad-attachment-fallback">
        <i className="fas fa-file-pdf"></i>
        {t('tree.pdfHint')}
      </span>
    );
  } else {
    media = (
      <span className="ad-attachment-fallback">
        <i className="fas fa-paperclip"></i>
        {attachment.name}
      </span>
    );
  }

  return (
    <>
      <span className="ad-attachment-preview">{media}</span>
      <span className="ad-attachment-links">
        <a className="ad-btn" href={url} target="_blank" rel="noreferrer">
          <i className="fas fa-up-right-from-square"></i>
          {t('tree.view')}
        </a>
        <a className="ad-btn" href={url} download={attachment.name}>
          <i className="fas fa-download"></i>
          {t('tree.download')}
        </a>
      </span>
    </>
  );
};

const attachmentNode = (a, t) => ({
  id: `attachment-${a.id}`,
  label: dash(a.name) || `#${a.id}`,
  sublabel: extensionOf(a.path).toUpperCase(),
  icon: KIND_ICON[kindOf(a.path)],
  details: {
    title: t('tree.attachmentDetails'),
    items: [
      { label: t('tree.fields.attachmentName'), value: a.name },
      { label: t('tree.fields.file'), value: attachmentPreview(a, t) },
    ],
  },
  edit: {
    title: t('tree.editAttachment'),
    fields: attachmentFields(t, false),
    initial: a,
    submit: (values) => propertyAttachmentAPI.update(a.id, attachmentBody(values)),
  },
  remove: () => propertyAttachmentAPI.delete(a.id),
});

// ---- leaves -------------------------------------------------------------

const ownershipNode = (o, t) => ({
  id: `ownership-${o.id}`,
  label: dash(o.ownershipNameAr) || dash(o.ownershipNameEng) || `#${o.id}`,
  sublabel: dash(o.ownershipNameEng),
  icon: 'fa-user',
  meta: dash(o.identityNumber) || t('tree.noIdentity'),
  details: {
    title: t('tree.ownerDetails'),
    items: [
      { label: t('tree.fields.nameAr'), value: o.ownershipNameAr },
      { label: t('tree.fields.nameEng'), value: o.ownershipNameEng },
      { label: t('tree.fields.identityNumber'), value: o.identityNumber },
      { label: t('tree.fields.mobileNumber'), value: o.mobileNumber },
      { label: t('tree.fields.email'), value: o.emailId },
      { label: t('tree.fields.bankAccount'), value: o.bankAccountNumber },
      { label: t('tree.fields.streetName'), value: o.streetName },
      { label: t('tree.fields.isAgent'), value: Boolean(o.isAgent) },
    ],
  },
  edit: {
    title: t('tree.editOwner'),
    fields: ownerFields(t),
    initial: o,
    submit: (values) => propertyOwnershipAPI.update(o.id, ownerPayload(values, o.propertyId)),
  },
  remove: () => propertyOwnershipAPI.delete(o.id),
});

const unitNode = (u, t) => ({
  id: `unit-${u.id}`,
  label: dash(u.unitNumber) || `${t('tree.unit')} #${u.id}`,
  sublabel: dash(u.unitType?.nameAr) || dash(u.unitType?.nameEng),
  icon: 'fa-door-open',
  details: {
    title: t('tree.unitDetails'),
    items: [
      { label: t('tree.fields.unitNumber'), value: u.unitNumber },
      { label: t('tree.fields.floorNumber'), value: u.floorNumber },
      { label: t('tree.fields.unitArea'), value: u.unitArea },
      { label: t('tree.fields.numberOfRooms'), value: u.numberOfRooms },
      { label: t('tree.fields.unitDirection'), value: u.unitDirection },
      { label: t('tree.fields.unitFinishing'), value: u.unitFinishing },
      { label: t('tree.fields.countOfParking'), value: u.countOfParking },
      { label: t('tree.fields.electricityMeter'), value: u.electricityMeter },
      { label: t('tree.fields.waterMeter'), value: u.waterMeter },
      { label: t('tree.fields.furnished'), value: Boolean(u.furnished) },
    ],
  },
  edit: {
    title: t('tree.editUnit'),
    fields: unitFields(t),
    initial: u,
    submit: (values) => propertyUnitAPI.update(u.id, unitPayload(values, u.propertyId)),
  },
  remove: () => propertyUnitAPI.delete(u.id),
});

const asDate = (value) => (value ? String(value).slice(0, 10) : null);

const contractNode = (c, t) => ({
  id: `contract-${c.id}`,
  label: dash(c.contractNumber) || `${t('tree.contract')} #${c.id}`,
  sublabel: dash(c.property?.nameAr) || dash(c.property?.nameEng),
  icon: 'fa-file-signature',
  meta: asDate(c.dateFrom),
  // Details and edit reuse the rich contract intake dialog.
  contractIntake: { id: c.id, label: dash(c.contractNumber) || `#${c.id}` },
  remove: () => contractAPI.delete(c.id),
});

// ---- property (fans out into units / contracts / owners) ----------------

const propertyChildren = (property, t, can) => async () =>
  [
  {
    id: `p${property.id}-units`,
    module: 'units',
    label: t('tree.groups.units'),
    icon: 'fa-layer-group',
    // Loaded from the dedicated endpoint so the column can be refetched after
    // an add/edit/delete without reloading the whole property.
    loadChildren: async () => {
      const { data } = await propertyUnitAPI.getByProperty(property.id);
      return (data || []).map((u) => guardNode(unitNode(u, t), 'units', can));
    },
    create: {
      title: t('tree.addUnit'),
      fields: unitFields(t),
      submit: (values) => propertyUnitAPI.create(unitPayload(values, property.id)),
    },
  },
  {
    id: `p${property.id}-contracts`,
    module: 'contracts',
    label: t('tree.groups.contracts'),
    icon: 'fa-file-contract',
    loadChildren: async () => {
      const res = await contractAPI.getAll('', 1, PAGE, null, property.id);
      return (res.data.items || []).map((c) => guardNode(contractNode(c, t), 'contracts', can));
    },
    // Handled by the dedicated contract intake dialog.
    createContract: { propertyId: property.id },
  },
  {
    id: `p${property.id}-owners`,
    module: 'owners',
    label: t('tree.groups.owners'),
    icon: 'fa-users',
    loadChildren: async () => {
      const { data } = await propertyOwnershipAPI.getByProperty(property.id);
      return (data || []).map((o) => guardNode(ownershipNode(o, t), 'owners', can));
    },
    create: {
      title: t('tree.addOwner'),
      fields: ownerFields(t),
      submit: (values) => propertyOwnershipAPI.create(ownerPayload(values, property.id)),
    },
  },
  {
    id: `p${property.id}-attachments`,
    module: 'attachments',
    label: t('tree.groups.attachments'),
    icon: 'fa-paperclip',
    loadChildren: async () => {
      const { data } = await propertyAttachmentAPI.getByProperty(property.id);
      return (data || []).map((a) => guardNode(attachmentNode(a, t), 'attachments', can));
    },
    create: {
      title: t('tree.addAttachment'),
      fields: attachmentFields(t, true),
      submit: (values) => propertyAttachmentAPI.create(attachmentBody(values, property.id)),
    },
  },
  // These two open a dialog instead of another column — `opens` tells the
  // browser which one, since the data layer knows nothing about the modals.
  {
    id: `p${property.id}-ledger`,
    module: 'contracts',
    label: t('tree.groups.ledger'),
    icon: 'fa-table-list',
    opens: 'ledger',
    target: { id: property.id, label: propertyLabel(property) },
  },
  {
    id: `p${property.id}-report`,
    module: 'properties',
    label: t('tree.groups.report'),
    icon: 'fa-chart-pie',
    opens: 'report',
    target: { id: property.id, label: propertyLabel(property) },
  },
  ]
    .filter((group) => can(group.module, 'View'))
    .map((group) => ({
      ...group,
      create: can(group.module, 'Create') ? group.create : undefined,
    }));

const propertyNode = (p, t, can) => ({
  id: `property-${p.id}`,
  label: propertyLabel(p),
  sublabel: dash(p.nameEng) !== propertyLabel(p) ? dash(p.nameEng) : null,
  icon: 'fa-building',
  meta: dash(p.code),
  loadChildren: propertyChildren(p, t, can),
  // Details and edit reuse the rich intake dialog.
  intake: { id: p.id, label: propertyLabel(p) },
  ledger: { id: p.id, label: propertyLabel(p) },
  report: { id: p.id, label: propertyLabel(p) },
  remove: () => propertyAPI.delete(p.id),
});

// ---- owner --------------------------------------------------------------

const ownerNode = (o, t, can) => ({
  id: `owner-${o.key}`,
  label: dash(o.nameAr) || dash(o.nameEng) || t('tree.noIdentity'),
  sublabel: dash(o.nameEng),
  icon: 'fa-user-tie',
  meta: t('tree.propertiesCount', { count: o.propertiesCount }),
  details: {
    title: t('tree.ownerDetails'),
    items: [
      { label: t('tree.fields.nameAr'), value: o.nameAr },
      { label: t('tree.fields.nameEng'), value: o.nameEng },
      { label: t('tree.fields.identityNumber'), value: o.identityNumber },
      { label: t('tree.fields.mobileNumber'), value: o.mobileNumber },
      { label: t('tree.fields.email'), value: o.emailId },
      { label: t('tree.groups.properties'), value: o.propertiesCount },
    ],
  },
  loadChildren: async () => {
    const { data } = await ownerAPI.getProperties(o.key);
    return (data || []).map((p) => guardNode(propertyNode(p, t, can), 'properties', can));
  },
  // An owner can only sign in if they have a national ID on file.
  edit: o.identityNumber
    ? {
        title: t('owner.profile.createAccount'),
        fields: [
          {
            name: 'identityNumber',
            label: t('owner.profile.identityNumber'),
            icon: 'fa-id-card',
            type: 'text',
            disabled: true,
          },
          {
            name: 'password',
            label: t('owner.profile.accountPassword'),
            placeholder: t('owner.profile.accountPassword'),
            icon: 'fa-lock',
            type: 'password',
            required: true,
          },
        ],
        initial: { identityNumber: o.identityNumber, password: '' },
        submit: (values) =>
          ownerAccountAPI.createAccount({
            identityNumber: o.identityNumber,
            password: values.password,
          }),
      }
    : undefined,
});

// ---- company documents (any file type) ---------------------------------

const documentFields = (t, requireFile) => [
  {
    name: 'name',
    label: t('tree.fields.documentName'),
    placeholder: t('tree.fields.documentName'),
    icon: 'fa-tag',
    type: 'text',
    required: true,
  },
  {
    name: 'file',
    label: requireFile ? t('tree.fields.file') : t('tree.fields.replaceFile'),
    type: 'file',
    required: requireFile,
  },
];

const documentBody = (values) => {
  const body = new FormData();
  body.append('name', values.name || '');
  if (values.file) body.append('file', values.file);
  return body;
};

const formatSize = (bytes) => {
  if (!bytes) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
};

const documentNode = (d, t) => ({
  id: `document-${d.id}`,
  label: dash(d.name) || `#${d.id}`,
  sublabel: extensionOf(d.path).toUpperCase(),
  icon: KIND_ICON[kindOf(d.path)],
  meta: formatSize(d.fileSize),
  details: {
    title: t('tree.documentDetails'),
    items: [
      { label: t('tree.fields.documentName'), value: d.name },
      { label: t('tree.fields.fileName'), value: d.fileName },
      { label: t('tree.fields.fileSize'), value: formatSize(d.fileSize) },
      { label: t('tree.fields.file'), value: attachmentPreview(d, t) },
    ],
  },
  edit: {
    title: t('tree.editDocument'),
    fields: documentFields(t, false),
    initial: d,
    submit: (values) => organizationDocumentAPI.update(d.id, documentBody(values)),
  },
  remove: () => organizationDocumentAPI.delete(d.id),
});

// ---- staff & permissions ------------------------------------------------

const STAFF_MODULES = [
  'properties',
  'units',
  'owners',
  'attachments',
  'contracts',
  'documents',
  'staff',
];

const staffFields = (t, isCreate) => [
  {
    name: 'userName',
    label: t('tree.fields.userName'),
    placeholder: t('tree.fields.userName'),
    icon: 'fa-user',
    type: 'text',
    required: isCreate,
    // The username identifies the login and is not editable afterwards.
    disabled: !isCreate,
  },
  {
    name: 'password',
    label: isCreate ? t('tree.fields.password') : t('tree.fields.newPassword'),
    placeholder: isCreate ? t('tree.fields.password') : t('tree.fields.leaveBlank'),
    icon: 'fa-lock',
    type: 'password',
    required: isCreate,
  },
  {
    name: 'fullName',
    label: t('tree.fields.fullName'),
    placeholder: t('tree.fields.fullName'),
    icon: 'fa-id-badge',
    type: 'text',
  },
  {
    name: 'email',
    label: t('tree.fields.email'),
    placeholder: t('tree.fields.email'),
    icon: 'fa-envelope',
    type: 'email',
  },
  {
    name: 'phoneNumber',
    label: t('tree.fields.mobileNumber'),
    placeholder: t('tree.fields.mobileNumber'),
    icon: 'fa-mobile-screen',
    type: 'text',
  },
  {
    name: 'permissions',
    label: t('staff.permissions.title'),
    type: 'permissions',
    modules: STAFF_MODULES,
    moduleLabel: (module) => t(`staff.modules.${module}`),
  },
];

const staffPayload = (values) => ({
  userName: values.userName,
  password: values.password || undefined,
  fullName: str(values.fullName),
  email: str(values.email),
  phoneNumber: str(values.phoneNumber),
  permissions: values.permissions || [],
});

const grantedSummary = (staff, t) => {
  if (staff.isOrgAdmin) return t('staff.fullAccess');
  const granted = (staff.permissions || []).filter(
    (p) => p.canView || p.canCreate || p.canEdit || p.canDelete
  ).length;
  return t('staff.modulesCount', { count: granted });
};

const staffNode = (s, t) => ({
  id: `staff-${s.id}`,
  label: dash(s.fullName) || dash(s.userName) || `#${s.id}`,
  sublabel: dash(s.userName),
  icon: s.isOrgAdmin ? 'fa-user-shield' : 'fa-user-gear',
  meta: grantedSummary(s, t),
  details: {
    title: t('tree.staffDetails'),
    items: [
      { label: t('tree.fields.fullName'), value: s.fullName },
      { label: t('tree.fields.userName'), value: s.userName },
      { label: t('tree.fields.email'), value: s.email },
      { label: t('tree.fields.mobileNumber'), value: s.phoneNumber },
      { label: t('staff.permissions.title'), value: permissionSummary(s, t) },
    ],
  },
  edit: {
    title: t('tree.editStaff'),
    fields: staffFields(t, false),
    initial: s,
    submit: (values) => staffAPI.update(s.id, staffPayload(values)),
  },
  // Admin accounts are protected server-side too.
  remove: s.isOrgAdmin ? undefined : () => staffAPI.delete(s.id),
});

/** Readable "module: actions" list for the details dialog. */
const permissionSummary = (staff, t) => {
  if (staff.isOrgAdmin) return t('staff.fullAccess');

  const rows = (staff.permissions || []).filter(
    (p) => p.canView || p.canCreate || p.canEdit || p.canDelete
  );
  if (rows.length === 0) return t('staff.noAccess');

  return (
    <span className="ad-perm-summary">
      {rows.map((p) => {
        const actions = [
          p.canView && t('staff.permissions.canView'),
          p.canCreate && t('staff.permissions.canCreate'),
          p.canEdit && t('staff.permissions.canEdit'),
          p.canDelete && t('staff.permissions.canDelete'),
        ].filter(Boolean);

        return (
          <span key={p.module} className="ad-perm-summary-row">
            <strong>{t(`staff.modules.${p.module}`)}</strong>
            {`: ${actions.join('، ')}`}
          </span>
        );
      })}
    </span>
  );
};

// ---- roots --------------------------------------------------------------

/**
 * `can(module, action)` comes from AuthContext. Nodes are stripped of the
 * actions the user lacks, and roots they cannot view are dropped entirely, so
 * a restricted user never meets a 403.
 */
export const buildRoots = (t, can = () => true) => {
  const roots = [
  {
    id: 'root-owners',
    module: 'owners',
    label: t('tree.roots.owners'),
    icon: 'fa-users',
    searchKey: 'searchOwners',
    loadChildren: async (search = '') => {
      const { data } = await ownerAPI.getAll(search, 1, PAGE);
      return (data.items || []).map((o) => ownerNode(o, t, can));
    },
  },
  {
    id: 'root-properties',
    module: 'properties',
    label: t('tree.roots.properties'),
    icon: 'fa-city',
    searchKey: 'searchProperties',
    loadChildren: async (search = '') => {
      const { data } = await propertyAPI.getAll(search, 1, PAGE);
      return (data.items || []).map((p) => guardNode(propertyNode(p, t, can), 'properties', can));
    },
    // Handled by the dedicated intake dialog, not the generic form.
    createIntake: true,
  },
  {
    id: 'root-contracts',
    module: 'contracts',
    label: t('tree.roots.contracts'),
    icon: 'fa-file-contract',
    searchKey: 'searchContracts',
    loadChildren: async (search = '') => {
      const { data } = await contractAPI.getAll(search, 1, PAGE);
      return (data.items || []).map((c) => guardNode(contractNode(c, t), 'contracts', can));
    },
    createContract: {},
  },
  {
    id: 'root-documents',
    module: 'documents',
    label: t('tree.roots.documents'),
    icon: 'fa-folder-open',
    loadChildren: async () => {
      const { data } = await organizationDocumentAPI.getAll();
      return (data || []).map((d) => guardNode(documentNode(d, t), 'documents', can));
    },
    create: {
      title: t('tree.addDocument'),
      fields: documentFields(t, true),
      submit: (values) => organizationDocumentAPI.create(documentBody(values)),
    },
  },
  {
    id: 'root-staff',
    module: 'staff',
    label: t('tree.roots.staff'),
    icon: 'fa-users-gear',
    searchKey: 'searchStaff',
    loadChildren: async (search = '') => {
      const { data } = await staffAPI.getAll(search);
      return (data || []).map((s) => guardNode(staffNode(s, t), 'staff', can));
    },
    create: {
      title: t('tree.addStaff'),
      fields: staffFields(t, true),
      submit: (values) => staffAPI.create(staffPayload(values)),
    },
  },
  ];

  return roots
    .filter((root) => can(root.module, 'View'))
    .map((root) => guardNode(root, root.module, can));
};
